/**
 * "A watchlist's items changed" signal, keyed by watchlist id. Fired from the
 * add/remove/reorder/delete mutations themselves so every caller (grid menus,
 * title-bar dropdown, onboarding, the panel's own box) reaches every panel
 * whose result depends on that list. Their rows are local state fed by
 * polling, so a tag invalidation alone never refreshes them.
 *
 * The user's other tabs and windows hear it too, over a BroadcastChannel, as
 * a 'remote' change: they refresh silently, and only the acting tab tells the
 * user what moved.
 */
export const WATCHLIST_ITEMS_CHANGED = 'watchlist:items-changed'

const RELAY_CHANNEL = 'edge-watchlist-items-changed'

export type WatchlistChangeKind = 'added' | 'removed' | 'reordered' | 'deleted'

export interface WatchlistItemsChange {
  watchlistId: number
  kind: WatchlistChangeKind
  /** Tickers that actually changed; empty for 'reordered' and 'deleted'. */
  tickers: string[]
  /** One id per change; groups the effect reports for a single notice. */
  changeId: string
  /** 'local' = this tab made the change; 'remote' = relayed from another tab. */
  origin: 'local' | 'remote'
}

const KINDS: ReadonlySet<string> = new Set<WatchlistChangeKind>([
  'added',
  'removed',
  'reordered',
  'deleted',
])

/**
 * Made once per tab, so a change relayed from another tab never shares an id
 * with one of this tab's (each tab counts its changes from 1).
 */
const TAB_TOKEN = makeTabToken()

function makeTabToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

let changeCounter = 0

function dispatchChange(change: WatchlistItemsChange): void {
  window.dispatchEvent(new CustomEvent(WATCHLIST_ITEMS_CHANGED, { detail: change }))
}

/** A relayed message as a change, or null when it is not one. */
function readRelayed(data: unknown): WatchlistItemsChange | null {
  const d = data as Partial<WatchlistItemsChange> | null
  if (!d || typeof d.watchlistId !== 'number' || typeof d.changeId !== 'string') return null
  if (typeof d.kind !== 'string' || !KINDS.has(d.kind) || !Array.isArray(d.tickers)) return null
  return {
    watchlistId: d.watchlistId,
    kind: d.kind,
    tickers: d.tickers.filter((t): t is string => typeof t === 'string'),
    changeId: d.changeId,
    origin: 'remote',
  }
}

/** Undefined until first needed; null where the browser has no BroadcastChannel. */
let relay: BroadcastChannel | null | undefined

function relayChannel(): BroadcastChannel | null {
  if (relay !== undefined) return relay
  if (typeof BroadcastChannel === 'undefined') {
    relay = null
    return relay
  }
  relay = new BroadcastChannel(RELAY_CHANNEL)
  // A channel never hears its own posts, so nothing relayed comes back here.
  relay.onmessage = (event: MessageEvent) => {
    const change = readRelayed(event.data)
    if (change) dispatchChange(change)
  }
  return relay
}

/** Call only after the server confirmed the change. */
export function notifyWatchlistItemsChanged(
  change: Pick<WatchlistItemsChange, 'watchlistId' | 'kind' | 'tickers'>,
): void {
  const full: WatchlistItemsChange = {
    watchlistId: change.watchlistId,
    kind: change.kind,
    tickers: [...change.tickers],
    changeId: `watchlist-change-${TAB_TOKEN}-${++changeCounter}`,
    origin: 'local',
  }
  dispatchChange(full)
  relayChannel()?.postMessage(full)
}

/**
 * Every change, local or relayed. Returns the unsubscribe. Opens the relay so
 * a tab whose panels listen hears the other tabs' changes.
 */
export function onWatchlistItemsChanged(cb: (change: WatchlistItemsChange) => void): () => void {
  relayChannel()
  const handler = (e: Event) => cb((e as CustomEvent<WatchlistItemsChange>).detail)
  window.addEventListener(WATCHLIST_ITEMS_CHANGED, handler)
  return () => window.removeEventListener(WATCHLIST_ITEMS_CHANGED, handler)
}
